/* cf-compare-table.js — assemble la matrice de comparaison.

   Lit `?p=<handles>` (D63), dédoublonne, replafonne à 4 (D61), puis résout
   chaque produit par `cf-compare-resolver.js` : Section Rendering, requêtes
   **parallèles**, tolérance par produit. Un produit en échec n'empêche pas les
   autres de s'afficher.

   **Purge conditionnelle.** Seul un 404 confirmé retire un handle de l'état
   canonique. Un échec transitoire laisse l'état intact et affiche un état
   d'erreur récupérable — la sélection stockée n'est jamais détruite sur un
   doute.

   La matrice est un vrai `<table>` : en-têtes de colonnes pour les produits,
   en-têtes de lignes pour les specs, relations préservées au lecteur d'écran.
   Le débordement horizontal est confiné au conteneur, jamais à la page.

   **La géométrie est réservée avant les requêtes.** Le nombre de colonnes est
   connu dès la lecture de `?p=` : on rend donc une ossature aux bonnes dimensions
   avant de résoudre, plutôt qu'une table qui grandit à mesure que les réponses
   arrivent. Sans cela, jusqu'à quatre réponses parallèles décaleraient la page à
   chaque arrivée — CLS garanti.

   **Ce fichier est le seul à résoudre.** Il n'est chargé que par `page.compare`.
   Le tiroir, lui, vit dans un groupe de sections et est donc présent sur toutes
   les pages : il ne fait aucune requête, jamais, et se contente de son cache de
   métadonnées. Faire résoudre le tiroir ferait payer jusqu'à quatre requêtes à
   chaque page du thème pour afficher trois vignettes.

   Une responsabilité, un fichier. */

import { resolveSelection, readHandlesFromUrl, normaliseHandles } from './cf-compare-resolver.js';

class CfCompareTable extends HTMLElement {
  connectedCallback() {
    this.matrix = this.querySelector('[data-cf-compare-matrix]');
    this.scroller = this.querySelector('[data-cf-compare-scroller]');
    this.stateEmpty = this.querySelector('[data-cf-compare-empty]');
    this.stateSingle = this.querySelector('[data-cf-compare-single]');
    this.stateError = this.querySelector('[data-cf-compare-error]');
    this.verdicts = this.querySelector('[data-cf-compare-verdicts]');
    this.verdictGrid = this.querySelector('[data-cf-compare-verdict-grid]');
    this.live = this.querySelector('[data-cf-compare-live]');
    if (!this.matrix) return;

    this.onRetry = this.onRetry.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onStoreChange = this.onStoreChange.bind(this);

    this.addEventListener('click', this.onRetry);
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('cf:compare:change', this.onStoreChange);

    this.load();
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    this.removeEventListener('click', this.onRetry);
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('cf:compare:change', this.onStoreChange);
  }

  get store() {
    return document.querySelector('cf-compare-store');
  }

  /* Contrat de précédence (phase 1) : `?p=` prime sur l'état stocké. Une URL
     partagée doit montrer ce qu'elle annonce. Sans `?p=`, on repart du store. */
  currentSelection() {
    const fromUrl = readHandlesFromUrl();
    if (fromUrl.length > 0) return fromUrl;
    const store = this.store;
    return normaliseHandles(store && store.items ? store.items : []);
  }

  async load() {
    const handles = this.currentSelection();

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    /* Jeton de propriété du rendu. Un `load()` plus récent remplace
       `this.controller` ; celui-ci saura alors qu'il ne possède plus le DOM. */
    const controller = this.controller;

    if (handles.length === 0) {
      this.showState('empty');
      return;
    }

    /* Géométrie réservée avant les requêtes : autant de colonnes que de handles
       demandés, hauteur de lignes fixée. Les réponses remplissent, elles ne
       repoussent rien. */
    this.renderSkeleton(handles.length);
    this.showState('matrix');
    this.setAttribute('aria-busy', 'true');

    const outcome = await resolveSelection(handles, {
      endpoint: this.dataset.endpoint,
      sectionId: this.dataset.sectionId,
      signal: this.controller.signal,
    });

    /* ------------------------------------------------------------------
       Un chargement supplanté n'écrit plus rien.

       Au premier rendu, deux `load()` se suivent : celui de
       `connectedCallback` et celui déclenché par l'hydratation du store
       (`cf:compare:change`). Le second appelle `this.controller.abort()`, ce
       qui fait **échouer les requêtes du premier** ; le premier lisait alors
       ces abandons comme des échecs transitoires et peignait l'état d'erreur
       par-dessus le résultat du second.

       Conséquence observée au rendu réel (phase 3 du sprint I) : un lien
       partagé dont tous les produits ont été supprimés affichait « Some
       products could not be loaded », qui annonce un incident passager, à la
       place de l'état vide — le seul honnête, puisque les produits n'existent
       plus.

       Un abandon n'est pas une panne : c'est une supplantation. Le chargement
       qui ne possède plus `this.controller` s'arrête sans toucher au DOM ni au
       store, et laisse le plus récent conclure.
       ------------------------------------------------------------------ */
    if (this.controller !== controller) return;

    this.removeAttribute('aria-busy');

    /* Purge : uniquement des absences confirmées. Les échecs transitoires ne
       touchent pas l'état canonique. */
    if (outcome.stale.length > 0) this.purge(outcome.stale);

    if (outcome.products.length === 0) {
      this.showState(outcome.transient > 0 ? 'error' : 'empty');
      return;
    }

    this.renderMatrix(outcome.products);
    this.renderVerdicts(outcome.products);

    if (outcome.products.length === 1) this.showState('single', true);
    else this.showState('matrix');

    /* Un produit manquant sur plusieurs valides : la matrice s'affiche et
       l'erreur est signalée sans effacer ce qui a abouti. */
    if (outcome.transient > 0 && this.stateError) this.stateError.hidden = false;

    this.syncStore(outcome.products.map((product) => product.handle));
  }

  purge(handles) {
    const store = this.store;
    if (!store || !store.remove) return;
    handles.forEach((handle) => store.remove(handle));
  }

  /* Le store reste le seul auteur de `localStorage` : on lui demande, on
     n'écrit pas. La synchronisation vaut aussi pour le tiroir, qui écoute le
     même événement — table et tiroir ne peuvent donc pas diverger. */
  syncStore(surviving) {
    const store = this.store;
    if (!store || !store.setSelection) return;
    store.setSelection(surviving);
  }

  showState(name, keepMatrix) {
    if (this.stateEmpty) this.stateEmpty.hidden = name !== 'empty';
    if (this.stateSingle) this.stateSingle.hidden = name !== 'single';
    if (this.stateError) this.stateError.hidden = name !== 'error';
    if (this.scroller) this.scroller.hidden = name === 'empty' || name === 'error';
    if (this.verdicts) {
      this.verdicts.hidden = name === 'empty' || name === 'error';
    }
    if (!keepMatrix && (name === 'empty' || name === 'error')) {
      this.matrix.textContent = '';
      if (this.verdictGrid) this.verdictGrid.textContent = '';
    }
  }

  /* ---------- Ossature ---------- */

  /* Six lignes : au-delà, la table dépasse de toute façon la ligne de flottaison
     et le remplissage n'est plus visible. En deçà, la réservation resterait
     insuffisante sur les fiches les mieux renseignées. */
  renderSkeleton(columns) {
    this.matrix.textContent = '';
    this.matrix.classList.add('cf-compare-table--loading');

    const head = document.createElement('thead');
    const headRow = document.createElement('tr');
    headRow.appendChild(this.cell('th', this.dataset.labelSpec, { scope: 'col' }));

    for (let index = 0; index < columns; index += 1) {
      const th = this.cell('th', '', { scope: 'col' });
      th.className = 'cf-compare-table__product';
      th.appendChild(this.skeletonBlock('cf-compare-skeleton__media'));
      th.appendChild(this.skeletonBlock('cf-compare-skeleton__line'));
      headRow.appendChild(th);
    }

    head.appendChild(headRow);
    this.matrix.appendChild(head);

    /* Lignes d'attente : le compte vient du serveur (`data-skeleton-rows`), qui
       connaît le cardinal du catalogue `spec_definition` — majorant exact du
       nombre de lignes réelles. Six en dernier recours seulement, si l'attribut
       manque. Mesuré en phase 5 du sprint I : six lignes pour cinq réelles
       faisaient rétrécir la table et remontaient le pied de page. */
    const skeletonRows = Math.max(1, parseInt(this.dataset.skeletonRows, 10) || 6);

    const body = document.createElement('tbody');
    for (let row = 0; row < skeletonRows; row += 1) {
      const tr = document.createElement('tr');
      const label = this.cell('th', '', { scope: 'row' });
      label.appendChild(this.skeletonBlock('cf-compare-skeleton__line'));
      tr.appendChild(label);
      for (let index = 0; index < columns; index += 1) {
        const td = document.createElement('td');
        td.className = 'cf-compare-table__value';
        td.appendChild(this.skeletonBlock('cf-compare-skeleton__line'));
        tr.appendChild(td);
      }
      body.appendChild(tr);
    }
    this.matrix.appendChild(body);
  }

  skeletonBlock(className) {
    const node = document.createElement('span');
    node.className = 'cf-compare-skeleton ' + className;
    node.setAttribute('aria-hidden', 'true');
    return node;
  }

  /* ---------- Matrice ---------- */

  renderMatrix(products) {
    this.matrix.classList.remove('cf-compare-table--loading');
    this.matrix.textContent = '';

    const head = document.createElement('thead');
    const headRow = document.createElement('tr');
    headRow.appendChild(this.cell('th', this.dataset.labelSpec, { scope: 'col' }));

    products.forEach((product) => {
      const th = this.cell('th', '', { scope: 'col' });
      th.className = 'cf-compare-table__product';
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = '';
        img.loading = 'lazy';
        img.className = 'cf-compare-table__image';
        th.appendChild(img);
      }
      const link = document.createElement('a');
      link.href = product.url;
      link.textContent = product.title;
      link.className = 'cf-compare-table__link';
      th.appendChild(link);

      const price = document.createElement('span');
      price.className = 'cf-compare-table__price cf-tabular';
      price.textContent = product.price || '';
      th.appendChild(price);

      headRow.appendChild(th);
    });

    head.appendChild(headRow);
    this.matrix.appendChild(head);

    const body = document.createElement('tbody');
    this.rowsFor(products).forEach((row) => body.appendChild(this.buildRow(row, products)));
    this.matrix.appendChild(body);
  }

  /* Une ligne n'existe que si au moins un produit porte la donnée. On ne
     fabrique jamais de ligne vide, et une donnée absente n'est pas une
     différence — c'est une absence. */
  rowsFor(products) {
    const order = [];
    const byKey = {};

    products.forEach((product) => {
      (product.specs || []).forEach((spec) => {
        if (!byKey[spec.key]) {
          byKey[spec.key] = { key: spec.key, label: spec.label, unit: spec.unit, values: {} };
          order.push(spec.key);
        }
        byKey[spec.key].values[product.handle] = spec.value;
      });
    });

    return order.map((key) => byKey[key]);
  }

  buildRow(row, products) {
    const tr = document.createElement('tr');
    tr.appendChild(this.cell('th', row.label, { scope: 'row' }));

    const present = products
      .map((product) => row.values[product.handle])
      .filter((value) => value !== undefined && value !== null && value !== '');

    /* Mise en évidence retenue : on ne souligne une différence que si **tous**
       les produits comparés portent la donnée. Sinon un trou deviendrait une
       fausse différence. */
    const complete = present.length === products.length;
    const differs = complete && new Set(present.map(String)).size > 1;
    if (differs && this.dataset.highlight === 'true') tr.classList.add('cf-compare-table__row--differs');

    products.forEach((product) => {
      const raw = row.values[product.handle];
      const td = document.createElement('td');
      td.className = 'cf-compare-table__value cf-tabular';

      if (raw === undefined || raw === null || raw === '') {
        td.textContent = this.dataset.labelAbsent || '—';
        td.classList.add('cf-compare-table__value--absent');
      } else if (raw === true || raw === false) {
        td.textContent = raw ? 'Yes' : 'No';
      } else {
        td.textContent = row.unit ? String(raw) + ' ' + row.unit : String(raw);
      }

      tr.appendChild(td);
    });

    return tr;
  }

  /* ---------- Bande éditoriale (D65) ---------- */

  renderVerdicts(products) {
    if (!this.verdictGrid) return;
    /* La prose est là : on rend la réserve à la mise en page réelle. */
    this.verdictGrid.textContent = '';

    let rendered = 0;
    products.forEach((product) => {
      if (!product.verdict) return;
      rendered += 1;

      const block = document.createElement('article');
      block.className = 'cf-compare-verdict';

      const title = document.createElement('h3');
      title.className = 'cf-compare-verdict__title';
      title.textContent = product.title;
      block.appendChild(title);

      /* Texte entier, jamais tronqué : le verdict est le différenciateur. */
      const body = document.createElement('div');
      body.className = 'cf-compare-verdict__body rte';
      body.innerHTML = product.verdict;
      block.appendChild(body);

      if (product.verdictAuthor && this.dataset.labelVerdictBy) {
        const author = document.createElement('p');
        author.className = 'cf-compare-verdict__author';
        author.textContent = this.dataset.labelVerdictBy.replace('%%author%%', product.verdictAuthor);
        block.appendChild(author);
      }

      this.verdictGrid.appendChild(block);
    });

    if (this.verdicts) this.verdicts.hidden = rendered === 0;
  }

  cell(tag, text, attributes) {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    Object.keys(attributes || {}).forEach((name) => node.setAttribute(name, attributes[name]));
    return node;
  }

  /* ---------- Événements ---------- */

  onRetry(event) {
    if (!event.target.closest('[data-cf-compare-retry]')) return;
    this.load();
  }

  onPopState() {
    this.load();
  }

  /* Retrait depuis le tiroir pendant que la page est ouverte : la table suit,
     et l'URL est réécrite pour rester partageable et cohérente au retour. */
  onStoreChange(event) {
    const items = event.detail ? event.detail.items : [];
    const url = new URL(window.location.href);
    if (items.length > 0) url.searchParams.set('p', items.join(','));
    else url.searchParams.delete('p');
    window.history.replaceState({}, '', url.toString());
    this.load();
  }
}

if (!customElements.get('cf-compare-table')) {
  customElements.define('cf-compare-table', CfCompareTable);
}
